import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowUpDown, ChevronDown, ChevronRight, ChevronUp, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/search-input';
import { PageHeader } from '@/components/PageHeader';
import { StatusBadge } from '@/components/StatusBadge';
import { StringMultiSelect } from '@/components/StringMultiSelect';
import { DesktopOnly } from '@/components/dashboard/DesktopOnly';
import { useRmmAlerts, type RmmInboxAlert } from '@/api/rmm';
import { fmtRelative } from '@/lib/cobalt-helpers';
import { alertPriorityKind, fmtAge } from '@/lib/rmm-helpers';
import { rmmDevicePath, ticketPath } from '@/lib/routes';
import { cn } from '@/lib/utils';
import { useScopeFilter } from '@/contexts/ClientScopeContext';

/**
 * Open-alerts inbox across every client. Read-only by decision: no resolve /
 * snooze / log-ticket here yet — it links to the device and to the Autotask
 * ticket when Datto's integration actually raised one.
 */

const PRIORITIES = ['Critical', 'High', 'Moderate', 'Low', 'Information'] as const;
const PRIORITY_OPTS = PRIORITIES.map((p) => ({ value: p, label: p }));
const priorityRank = (p: string) => {
  const i = (PRIORITIES as readonly string[]).indexOf(p);
  return i === -1 ? PRIORITIES.length : i;
};

type TicketFilter = 'any' | 'with' | 'without';

interface AlertFilters {
  search: string;
  priorities: string[];
  kinds: string[];
  clients: string[];
  ticket: TicketFilter;
  showMuted: boolean;
}

// Information alerts (mostly OneDrive sync monitors) are hidden by default — one click shows them.
const DEFAULT_FILTERS: AlertFilters = {
  search: '', priorities: ['Critical', 'High', 'Moderate', 'Low'], kinds: [], clients: [], ticket: 'any', showMuted: false,
};

/**
 * Pre-filtered views linked from the sidebar Views and dashboards (`rmmAlertsView` in
 * routes.ts): `priority=Critical,High`, `ticket=with|without`, `kind=<type>`, `client=<name>`.
 * A view with `kind` or `client` but no `priority` spans every priority. Returns null when the
 * URL carries none.
 */
function filtersFromSearch(sp: URLSearchParams): Partial<AlertFilters> | null {
  const f: Partial<AlertFilters> = {};
  const priority = sp.get('priority');
  if (priority) {
    const ps = priority.split(',').filter((p) => (PRIORITIES as readonly string[]).includes(p));
    if (ps.length) f.priorities = ps;
  }
  const ticket = sp.get('ticket');
  if (ticket === 'with' || ticket === 'without') {
    f.ticket = ticket;
    if (!priority) f.priorities = []; // "has a ticket" means any priority, including Information
  }
  if (sp.get('kind')) f.kinds = [sp.get('kind')!];
  if (sp.get('client')) f.clients = [sp.get('client')!];
  if ((f.kinds || f.clients) && !priority) f.priorities = [];
  return Object.keys(f).length ? f : null;
}

type SortKey = 'priority' | 'raised' | 'device' | 'client';
type SortState = { key: SortKey; dir: 'asc' | 'desc' };

const STORAGE_KEY = 'cosmos:rmm:alerts';

function load<T>(key: string, fallback: T): T {
  try { const r = localStorage.getItem(key); return r ? JSON.parse(r) as T : fallback; } catch { return fallback; }
}
function save(key: string, value: unknown) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* storage unavailable */ }
}

const clientName = (a: RmmInboxAlert) => a.autotaskCompanyName ?? a.siteName ?? '—';

export default function RmmAlertsPage() {
  const [filters, setFilters] = useState<AlertFilters>(() => ({ ...DEFAULT_FILTERS, ...load<Partial<AlertFilters>>(STORAGE_KEY, {}) }));
  const [sort, setSort] = useState<SortState>(() => load<SortState>(`${STORAGE_KEY}:sort`, { key: 'priority', dir: 'asc' }));
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // A menu link's view replaces the saved filters, then leaves the URL so it doesn't stick.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const view = filtersFromSearch(searchParams);
    if (!view) return;
    setFilters({ ...DEFAULT_FILTERS, ...view });
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => save(STORAGE_KEY, filters), [filters]);
  useEffect(() => save(`${STORAGE_KEY}:sort`, sort), [sort]);

  const alerts = useRmmAlerts({ scoped: true });
  // While a client is picked in the header, this page's own Client filter is ignored (and hidden).
  const { companyId: scopeId } = useScopeFilter();
  const clients = scopeId != null ? [] : filters.clients;
  const items = useMemo(() => alerts.data?.items ?? [], [alerts.data]);

  const kindOpts = useMemo(() => [...new Set(items.map((a) => a.kind))].sort().map((v) => ({ value: v, label: v })), [items]);
  const clientOpts = useMemo(
    () => [...new Set(items.map(clientName))].sort((a, b) => a.localeCompare(b)).map((v) => ({ value: v, label: v })),
    [items],
  );

  const q = filters.search.trim().toLowerCase();
  const filtered = useMemo(() => items.filter((a) => {
    if (!filters.showMuted && a.muted) return false;
    if (filters.priorities.length && !filters.priorities.includes(a.priority)) return false;
    if (filters.kinds.length && !filters.kinds.includes(a.kind)) return false;
    if (clients.length && !clients.includes(clientName(a))) return false;
    if (filters.ticket === 'with' && !a.ticketNumber) return false;
    if (filters.ticket === 'without' && a.ticketNumber) return false;
    if (q && ![a.summary, a.kind, a.deviceName, clientName(a), a.siteName, a.ticketNumber].some((v) => v?.toLowerCase().includes(q))) return false;
    return true;
  }), [items, filters, q, clients]);

  const sorted = useMemo(() => {
    const dir = sort.dir === 'asc' ? 1 : -1;
    const newestFirst = (a: RmmInboxAlert, b: RmmInboxAlert) => b.timestamp.localeCompare(a.timestamp);
    return [...filtered].sort((a, b) => {
      let c = 0;
      switch (sort.key) {
        case 'priority': c = priorityRank(a.priority) - priorityRank(b.priority); break;
        case 'raised':   c = a.timestamp.localeCompare(b.timestamp); break;
        case 'device':   c = (a.deviceName ?? '').localeCompare(b.deviceName ?? ''); break;
        case 'client':   c = clientName(a).localeCompare(clientName(b)); break;
      }
      // Ties (e.g. same priority) read best newest-first regardless of direction.
      return c !== 0 ? c * dir : newestFirst(a, b);
    });
  }, [filtered, sort]);

  function cycleSort(key: SortKey) {
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'raised' ? 'desc' : 'asc' }));
  }

  function toggle(uid: string) {
    setExpanded((prev) => { const next = new Set(prev); next.has(uid) ? next.delete(uid) : next.add(uid); return next; });
  }

  const isDefault = JSON.stringify(filters) === JSON.stringify(DEFAULT_FILTERS);
  const set = <K extends keyof AlertFilters>(k: K, v: AlertFilters[K]) => setFilters((f) => ({ ...f, [k]: v }));

  return (
    <DesktopOnly
      title="Alerts are available on desktop"
      message="Open Cosmos on a larger screen to view RMM alerts. The mobile app focuses on tickets and opportunities."
    >
      <div className="mx-auto w-full max-w-[1900px] px-4 py-4">
        <PageHeader
          title="Alerts"
          count={alerts.data ? filtered.length : null}
          isFetching={alerts.isFetching}
          subtitle={<>Datto RMM · Open alerts, all clients{alerts.data && <> · Updated {fmtRelative(alerts.data.fetchedAt)}</>}</>}
        />

        <PriorityStrip items={items} showMuted={filters.showMuted} onPick={(p) => setFilters({ ...DEFAULT_FILTERS, ...p })} />

        <div className="sticky top-0 z-10 -mx-4 mt-2 border-b bg-background/80 px-4 py-3 backdrop-blur">
          <div className="flex flex-wrap items-center gap-2">
            <SearchInput withIcon placeholder="Alert, device, client, ticket…" className="w-64" value={filters.search} onValueChange={(v) => set('search', v)} />
            <StringMultiSelect label="Priority" options={PRIORITY_OPTS} selected={filters.priorities} onChange={(v) => set('priorities', v)} />
            <StringMultiSelect label="Type" options={kindOpts} selected={filters.kinds} onChange={(v) => set('kinds', v)} />
            {scopeId == null && <StringMultiSelect label="Client" options={clientOpts} selected={filters.clients} onChange={(v) => set('clients', v)} />}
            <select
              value={filters.ticket}
              onChange={(e) => set('ticket', e.target.value as TicketFilter)}
              className={cn('h-8 rounded-md border border-input bg-background px-2 text-sm text-foreground', filters.ticket !== 'any' && 'bg-secondary')}
            >
              <option value="any">Any ticket status</option>
              <option value="with">Has a ticket</option>
              <option value="without">No ticket</option>
            </select>
            <Button type="button" size="sm" variant={filters.showMuted ? 'secondary' : 'outline'} aria-pressed={filters.showMuted} onClick={() => set('showMuted', !filters.showMuted)}>
              Show muted
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setFilters(DEFAULT_FILTERS)} disabled={isDefault} title={isDefault ? 'Filters are at defaults' : 'Reset filters'}>
              <RotateCcw className="mr-1 h-3 w-3" />Reset filters
            </Button>
          </div>
        </div>

        {alerts.error ? (
          <div className="mt-6 rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
            Failed to load alerts: {alerts.error instanceof Error ? alerts.error.message : 'Unknown error'}
          </div>
        ) : null}

        <div className="mt-4 overflow-x-auto rounded-md border">
          <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: 36 }} />
              <col style={{ width: 120 }} />
              <col />
              <col style={{ width: 210 }} />
              <col style={{ width: 230 }} />
              <col style={{ width: 100 }} />
              <col style={{ width: 150 }} />
            </colgroup>
            <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th />
                <SortHeader label="Priority" k="priority" sort={sort} onSort={cycleSort} />
                <th className="px-3 py-2 text-left font-medium normal-case">Alert</th>
                <SortHeader label="Device" k="device" sort={sort} onSort={cycleSort} />
                <SortHeader label="Client" k="client" sort={sort} onSort={cycleSort} />
                <SortHeader label="Raised" k="raised" sort={sort} onSort={cycleSort} />
                <th className="px-3 py-2 text-left font-medium normal-case">Ticket</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {alerts.isLoading ? (
                <tr><td colSpan={7} className="px-3 py-12 text-center text-muted-foreground">Loading alerts…</td></tr>
              ) : sorted.length === 0 ? (
                <tr><td colSpan={7} className="px-3 py-12 text-center text-muted-foreground">No alerts match these filters.</td></tr>
              ) : null}
              {sorted.map((a) => {
                const open = expanded.has(a.alertUid);
                return (
                  <Fragment key={a.alertUid}>
                    <tr className="hover:bg-muted/30">
                      <td className="px-2 py-2 align-top">
                        {a.diagnostics && (
                          <button type="button" onClick={() => toggle(a.alertUid)} title={open ? 'Hide diagnostics' : 'Show diagnostics'}
                            className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-accent-foreground">
                            {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                          </button>
                        )}
                      </td>
                      <td className="px-3 py-2 align-top">
                        <StatusBadge kind={alertPriorityKind(a.priority)}>{a.priority}</StatusBadge>
                      </td>
                      <td className="overflow-hidden px-3 py-2 align-top">
                        <div className="truncate text-foreground" title={a.summary}>{a.summary}</div>
                        <div className="truncate text-xs text-muted-foreground">{a.kind}{a.muted && ' · Muted'}</div>
                      </td>
                      <td className="overflow-hidden px-3 py-2 align-top">
                        {a.deviceUid ? (
                          <Link to={rmmDevicePath(a.deviceUid)} className="flex items-center gap-1.5 truncate font-medium hover:underline">
                            {a.deviceOnline != null && (
                              <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', a.deviceOnline ? 'bg-success' : 'bg-neutral')}
                                title={a.deviceOnline ? 'Online' : 'Offline'} />
                            )}
                            <span className="truncate">{a.deviceName}</span>
                          </Link>
                        ) : <span className="text-muted-foreground">{a.deviceName ?? '—'}</span>}
                        {a.deviceCategory && <div className="truncate text-xs text-muted-foreground">{a.deviceCategory}</div>}
                      </td>
                      <td className="overflow-hidden px-3 py-2 align-top">
                        <div className="truncate" title={clientName(a)}>{clientName(a)}</div>
                        {a.autotaskCompanyId == null && <div className="text-[11px] text-warning">unlinked site</div>}
                      </td>
                      <td className="px-3 py-2 align-top text-xs leading-5 text-muted-foreground" title={new Date(a.timestamp).toLocaleString()}>
                        {fmtAge(a.timestamp)} ago
                      </td>
                      <td className="overflow-hidden px-3 py-2 align-top text-xs leading-5">
                        {a.ticketNumber ? (
                          a.ticketId != null ? (
                            <Link to={ticketPath(a.ticketId)} className="font-medium text-brand-strong hover:underline dark:text-brand">{a.ticketNumber}</Link>
                          ) : (
                            <span className="text-muted-foreground line-through" title="This ticket no longer exists in Autotask (deleted or merged)">
                              {a.ticketNumber}
                            </span>
                          )
                        ) : <span className="text-muted-foreground">—</span>}
                      </td>
                    </tr>
                    {open && a.diagnostics && (
                      <tr className="bg-muted/20">
                        <td />
                        <td colSpan={6} className="px-3 pb-3 pt-1">
                          <pre className="max-h-72 overflow-auto whitespace-pre-wrap rounded bg-muted/50 p-2 font-mono text-[11px] text-foreground">{a.diagnostics}</pre>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </DesktopOnly>
  );
}

function SortHeader({ label, k, sort, onSort }: { label: string; k: SortKey; sort: SortState; onSort: (k: SortKey) => void }) {
  const active = sort.key === k;
  const Icon = !active ? ArrowUpDown : sort.dir === 'asc' ? ChevronUp : ChevronDown;
  return (
    <th className="px-3 py-2 text-left font-medium">
      <button type="button" onClick={() => onSort(k)}
        className={cn('inline-flex items-center gap-1 transition-colors', active ? 'text-foreground' : 'hover:text-foreground')}>
        {label}<Icon className={cn('h-3 w-3', active ? 'opacity-80' : 'opacity-40')} />
      </button>
    </th>
  );
}

/** Count per priority (over all open alerts, ignoring other filters); click to show just that priority. */
function PriorityStrip({ items, showMuted, onPick }: { items: RmmInboxAlert[]; showMuted: boolean; onPick: (f: Partial<AlertFilters>) => void }) {
  const visible = items.filter((a) => showMuted || !a.muted);
  const withTicket = visible.filter((a) => a.ticketNumber).length;
  return (
    <div className="my-4 grid grid-cols-6 gap-2">
      {PRIORITIES.map((p) => {
        const n = visible.filter((a) => a.priority === p).length;
        const kind = alertPriorityKind(p);
        return (
          <button key={p} type="button" onClick={() => onPick({ priorities: [p], showMuted })} title={`Show ${p} alerts`}
            className="rounded-md border bg-card px-3 py-2.5 text-left shadow-sm transition-colors hover:bg-muted/40">
            <div className="text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground">{p}</div>
            <div className={cn('mt-1 text-[20px] font-[650] tabular-nums tracking-[-0.02em]',
              n > 0 && kind === 'destructive' && 'text-destructive',
              n > 0 && kind === 'warning' && 'text-warning')}>{n}</div>
          </button>
        );
      })}
      <button type="button" onClick={() => onPick({ priorities: [], ticket: 'with', showMuted })} title="Show alerts that raised an Autotask ticket"
        className="rounded-md border bg-card px-3 py-2.5 text-left shadow-sm transition-colors hover:bg-muted/40">
        <div className="text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground">With a ticket</div>
        <div className="mt-1 text-[20px] font-[650] tabular-nums tracking-[-0.02em]">{withTicket}</div>
      </button>
    </div>
  );
}
